import React, { FC, useCallback, useMemo, useState } from 'react';

import styled from 'styled-components';

import aampeTheme from '../../constants/aampeTheme';
import ICONS from '../../constants/icons';

import useHandleQueryParams from '../../hooks/helper/useHandleQueryParams';
import useOutsideClickDetector from '../../hooks/useOutsideClickDetector';

import BasicButton from '../buttons/basicButton';

interface SortButtonPopupProps {
    sortOptions: {
        [K in string]: { sort_by: string; is_sorted_desc: string };
    };
}

const SortButtonPopup: FC<SortButtonPopupProps> = ({ sortOptions }) => {
    const [isPopupVisible, setIsPopupVisible] = useState<boolean>(false);
    const addButtonRef = useOutsideClickDetector(() =>
        setIsPopupVisible(false),
    );

    const [queryParams, setQueryParams] = useHandleQueryParams();

    const selectedSortType = useMemo(
        () =>
            Object.keys(sortOptions).find((key) => {
                return (
                    sortOptions[key].sort_by === queryParams.sort_by &&
                    sortOptions[key].is_sorted_desc ===
                        queryParams.is_sorted_desc
                );
            }),
        [queryParams, sortOptions],
    );

    const handleClick = useCallback(
        (item: string) => {
            const sortParams = sortOptions[item] || {};
            setQueryParams({
                sort_by: String(sortParams.sort_by),
                is_sorted_desc: String(sortParams.is_sorted_desc),
            });
        },
        [setQueryParams, sortOptions],
    );

    return (
        <Wrapper>
            <StyledDiv ref={addButtonRef}>
                <StyledBasicButton
                    isPrimary={false}
                    isCompact
                    name="Sort"
                    icon={<ICONS.filter />}
                    isTrailingIcon
                    action={() => setIsPopupVisible(!isPopupVisible)}
                />
            </StyledDiv>
            {isPopupVisible && (
                <ListContainer>
                    {Object.keys(sortOptions)?.map((item) => (
                        <ListItem
                            selected={selectedSortType === item}
                            key={item}
                            onClick={() => handleClick(item)}
                        >
                            {item}
                        </ListItem>
                    ))}
                </ListContainer>
            )}
        </Wrapper>
    );
};

const Wrapper = styled.div({
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
});

const StyledDiv = styled.div({ position: 'relative', display: 'inline-block' });

const ListContainer = styled.div({
    display: 'flex',
    flexDirection: 'column',
    gap: aampeTheme.spacing4(2),
    overflow: 'auto',
    position: 'absolute',
    top: '36px',
    right: '45px',
    backgroundColor: aampeTheme.colors.white,
    borderRadius: aampeTheme.borderRadius.default,
    boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
    padding: aampeTheme.spacing4(2),
    zIndex: 101,
    width: '180px',
});

const ListItem = styled.span<{ selected: boolean }>(({ selected }) => ({
    cursor: 'pointer',
    fontSize: aampeTheme.size.font.default,
    backgroundColor: selected ? aampeTheme.colors.bordersWG01 : '',
    lineHeight: aampeTheme.size.lineHeight.small,
    fontWeight: aampeTheme.weights.light,
    padding: aampeTheme.spacing4(2),
    '&:hover': {
        backgroundColor: aampeTheme.colors.bordersWG01,
        color: aampeTheme.colors.solidA01,
    },
}));

const StyledBasicButton = styled(BasicButton)({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '36px',
    fontSize: aampeTheme.size.font.textMedium,
    fontWeight: aampeTheme.weights.medium,
    lineHeight: aampeTheme.size.lineHeight.default,
    color: aampeTheme.colors.solidA01,
});

export default SortButtonPopup;
